import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

interface AuthContextType {
  isAuthenticated: boolean;
  userInfo: { username: string; email: string; user_type: string; must_change_password: boolean };
  isAdmin: boolean;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  validateToken: (token: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return "http://dynasty-app.io/api"; // Production URL
  }
  return "http://localhost:8080/api"; // Development URL
};

const API_BASE_URL = process.env.REACT_APP_API_URL || getApiBaseUrl();

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState({ username: "", email: "", user_type: "", must_change_password: false });

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/login/`, {
        email,
        password: password,
      });

      const token = response.data.token;
      localStorage.setItem("token", token);
      axios.defaults.headers.common["Authorization"] = `Token ${token}`;
      console.log("Token set in localStorage:", token);
      setIsAuthenticated(true);
      setUserInfo({
        username: response.data.username ?? "",
        email: response.data.email ?? "",
        user_type: response.data.user_type ?? "",
        must_change_password: Boolean(response.data.must_change_password),
      });
    } catch (error) {
      console.error("Login error details:", error);
      if (axios.isAxiosError(error)) {
        console.error("Server response status:", error.response?.status);
        console.error("Server response headers:", error.response?.headers);
        console.error("Server response data:", error.response?.data);
        console.error("Request config:", error.config);
      }
      throw new Error("Invalid email or password");
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    setUserInfo({ username: "", email: "", user_type: "", must_change_password: false });
  };

  const validateToken = async (token: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/validate-token/`, {
        headers: { Authorization: `Token ${token}` },
      });
      setUserInfo({
        username: response.data.username ?? "",
        email: response.data.email ?? "",
        user_type: response.data.user_type ?? "",
        must_change_password: Boolean(response.data.must_change_password),
      });
      return true;
    } catch (error) {
      console.error("Token validation failed:", error);
      logout();
      return false;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Token ${token}`;
      validateToken(token).then((isValid) => {
        setIsAuthenticated(isValid);
      });
    }
  }, []);
  const isAdmin = userInfo.user_type === "ADMIN";
  const mustChangePassword = userInfo.must_change_password;

  return (
    <AuthContext.Provider value={{ isAuthenticated, userInfo, isAdmin, mustChangePassword, login, logout, validateToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 